import React, {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useState,
} from 'react';
import { OfferLineItem, TRADE_OFFER_MAX_CARDS_PER_SIDE } from 'types/interfaces/tradeOffer';
import { clearOfferBuilderSeed, readOfferBuilderSeed } from 'utils/offerBuilderSeed';

export type OfferSide = 'youSend' | 'theySend';

/**
 * A have row this builder session CREATED via an un-catalogued add, recorded
 * so removing the card can undo the row instead of stranding it.
 *
 * `createdQuantity` is what separates a create from an increment: the add
 * endpoint upserts on (userId, cardId, foil, condition), so a row that came
 * back at exactly the quantity we asked for did not exist before and may be
 * deleted outright on undo. Anything larger means the sender ALREADY OWNED
 * copies and the row must only ever be decremented — deleting it would
 * destroy inventory they catalogued themselves.
 */
export interface BuilderCreatedRow {
  userMTGCardHaveId: number;
  /** Quantity this session added (1 per add today) */
  addedQuantity: number;
  /** True only when the add created the row rather than incrementing one */
  wasCreated: boolean;
}

interface OfferBuilderContextType {
  counterpartyId: number;
  counterpartyUsername: string;
  /**
   * Live balances for the coverage rule. Deliberately NOT part of the persisted
   * session: they are props straight through to the context value, never folded
   * into `state`. Persisting them would demand a STORAGE_SCHEMA_VERSION bump and,
   * worse, could restore a stale balance into a funding decision. Null means
   * unknown, and every consumer must render no coverage UI rather than guess.
   */
  viewerPoints: number | null;
  counterpartyPoints: number | null;
  youSend: OfferLineItem[];
  theySend: OfferLineItem[];
  suggestionId: string | undefined;

  youSendTotal: number;
  theySendTotal: number;
  youSendAtCap: boolean;
  theySendAtCap: boolean;

  addItem: (side: OfferSide, item: OfferLineItem) => void;
  removeItem: (side: OfferSide, userMTGCardHaveId: number) => void;
  /**
   * youSend only: declare the condition the sender will ship. Swaps the line's
   * unit price to the declared condition's quote, so the per-card price, side
   * total, net, and balance bar all move in the same interaction.
   */
  setItemCondition: (userMTGCardHaveId: number, condition: string) => void;
  setItemQuantity: (side: OfferSide, userMTGCardHaveId: number, quantity: number) => void;
  updateItemOwnedQuantity: (side: OfferSide, userMTGCardHaveId: number, ownedQuantity: number) => void;
  getAddedQuantity: (side: OfferSide, userMTGCardHaveId: number) => number;
  /**
   * Total copies already charged to ONE want record, summed across every
   * printing that satisfies it. The want record is the unit the recipient
   * agreed to — a want of 1 is 1 card, not 1 per set — so this cannot be keyed
   * by have row the way getAddedQuantity is. Keyed by want-entry id and never
   * by oracleId: several independent want records may exist for the same card
   * (the (userId, oracleId) unique index was deliberately dropped so someone
   * can want 4 black-bordered AND 4 gold-bordered), and aggregating them would
   * wrongly cap a sender at 4 when the recipient wants 8.
   */
  getWantEntryAddedQuantity: (side: OfferSide, wantEntryId: number) => number;
  /**
   * Record the outcome of an un-catalogued add. `rowQuantityAfterAdd` is the
   * quantity the server returned; passing it here (rather than a boolean
   * decided at the call site) keeps the create-vs-increment rule in one place.
   */
  markRowCreatedByBuilder: (
    userMTGCardHaveId: number,
    rowQuantityAfterAdd: number,
    addedQuantity?: number
  ) => void;
  /** What this session did to that have row, or undefined if it never touched it */
  getBuilderCreatedRow: (userMTGCardHaveId: number) => BuilderCreatedRow | undefined;
  /** Forget one mark — after its undo has been applied */
  forgetBuilderCreatedRow: (userMTGCardHaveId: number) => void;
  /**
   * The line the server named in an OFFER_EXCEEDS_WANT_QUANTITY rejection, so
   * the column can point at it instead of leaving a toast to describe a card
   * by a name three lines share.
   *
   * Deliberately NOT persisted: it describes one failed submit, not the
   * package, and restoring it would accuse a line the current package may no
   * longer contain. Cleared as soon as the package changes, because any edit
   * invalidates the server's verdict.
   */
  rejectedHaveId: number | null;
  setRejectedHaveId: (userMTGCardHaveId: number | null) => void;
  clearAll: () => void;
}

const OfferBuilderContext = createContext<OfferBuilderContextType | undefined>(undefined);

const STORAGE_KEY = 'trade_offer_builder';

/**
 * Persisted-session schema version. Bumped when the OfferLineItem shape gains
 * fields the builder's behavior depends on (v2: acceptedConditions +
 * quotedPricesByCondition from the condition-selector work; v3: createdRows,
 * which decides whether an undo may DELETE a have row or only decrement it).
 * A session persisted by an older build is discarded rather than restored, so
 * items predating a constraint can never resurrect without it - the builder
 * simply reseeds from live data.
 *
 * v3 matters more than a display field would: a v2 session restored under v3
 * would carry offer lines with no createdRows record, and every removal would
 * then look like "a row the sender already owned" — safe, but it would strand
 * the rows this feature creates. Discarding is the correct trade.
 *
 * v4 (originatingWantEntryId) is the same trade for a sharper failure. The
 * full-want-list row derives its "Added" state from that field, so a v3 session
 * restored under v4 would render "Add" for a card already sitting in the
 * package — and clicking it re-enters the un-catalogued add to create a have
 * row that already exists, colliding with the (userId, cardId, foil, condition)
 * upsert key. That state is unrecoverable, because the want-entry mapping was
 * never persisted to restore. Losing one in-flight package on one deploy beats
 * shipping this feature's own failure mode as the migration path.
 *
 * v5 extends originatingWantEntryId from un-catalogued adds to EVERY send-side
 * line, because it is now the key the want-quantity budget is summed over. A v4
 * session restored under v5 would carry ordinary candidate lines with the field
 * absent, so every one of them would be invisible to that budget: the builder
 * would reopen showing "Add" on printings whose want is already spent, which is
 * precisely the over-commitment this version exists to prevent. The field
 * cannot be backfilled on restore either — the want-entry mapping lives in the
 * candidates response, not in the persisted session. Discarding is again the
 * correct trade.
 *
 * v6 (Track D, finish): every line carries `finish` (NONFOIL | FOIL | ETCHED)
 * with `foil` as its derived mirror. A v5 session carries only `foil`, so an
 * ETCHED line restored as FOIL would present the wrong copy against the new
 * (userId, cardId, finish, condition) key and could be sent as one. Discarding
 * is the correct trade once more.
 */
const STORAGE_SCHEMA_VERSION = 6;

interface StorageData {
  version?: number;
  counterpartyId: number;
  counterpartyUsername: string;
  youSend: OfferLineItem[];
  theySend: OfferLineItem[];
  suggestionId?: string;
  /**
   * Persisted alongside the lines so a refresh mid-build does not lose the
   * knowledge of which rows this session created — losing it would strand
   * un-catalogued adds in the sender's collection.
   */
  createdRows?: BuilderCreatedRow[];
}

const sideTotal = (items: OfferLineItem[]): number =>
  items.reduce((sum, item) => sum + item.unitPricePoints * item.quantity, 0);

/** Merge seed items into existing items, skipping have rows already present */
const mergeItems = (existing: OfferLineItem[], seeded: OfferLineItem[]): OfferLineItem[] => {
  const present = new Set(existing.map((i) => i.userMTGCardHaveId));
  const additions = seeded.filter((i) => !present.has(i.userMTGCardHaveId));
  return [...existing, ...additions].slice(0, TRADE_OFFER_MAX_CARDS_PER_SIDE);
};

export const OfferBuilderProvider: React.FC<{
  counterpartyId: number;
  counterpartyUsername: string;
  viewerPoints: number | null;
  counterpartyPoints: number | null;
  children: React.ReactNode;
}> = ({ counterpartyId, counterpartyUsername, viewerPoints, counterpartyPoints, children }) => {
  // Initialize synchronously from storage so the seed is consumed exactly once
  // and the first render already shows seeded items (no flash of empty columns).
  const [state, setState] = useState<StorageData>(() => {
    let base: StorageData = {
      counterpartyId,
      counterpartyUsername,
      youSend: [],
      theySend: [],
      createdRows: [],
    };
    try {
      const stored = sessionStorage.getItem(STORAGE_KEY);
      if (stored) {
        const data: StorageData = JSON.parse(stored);
        // A persisted session for a different counterparty, or one written by
        // an older build (schema version mismatch), is discarded
        if (data.counterpartyId === counterpartyId && data.version === STORAGE_SCHEMA_VERSION) {
          base = {
            ...base,
            youSend: data.youSend || [],
            theySend: data.theySend || [],
            suggestionId: data.suggestionId,
            createdRows: data.createdRows || [],
          };
        }
      }
    } catch (e) {
      console.error('Failed to load offer builder from storage:', e);
    }

    // Consume the seed idempotently: merging is a no-op on re-runs, and the
    // clear happens in an effect below — clearing here would lose the seed to
    // StrictMode's dev double-mount (initializers run on both mounts).
    const seed = readOfferBuilderSeed();
    if (seed && seed.counterparty.id === counterpartyId) {
      if (seed.suggestionId) {
        // A suggestion seeds the complete bundle: replace, and carry the id
        // through to the POST body so the backend can release the allocation.
        base = {
          ...base,
          youSend: seed.youSend.slice(0, TRADE_OFFER_MAX_CARDS_PER_SIDE),
          theySend: seed.theySend.slice(0, TRADE_OFFER_MAX_CARDS_PER_SIDE),
          suggestionId: seed.suggestionId,
        };
      } else {
        base = {
          ...base,
          youSend: mergeItems(base.youSend, seed.youSend),
          theySend: mergeItems(base.theySend, seed.theySend),
        };
      }
    }
    return base;
  });

  useEffect(() => {
    clearOfferBuilderSeed();
  }, []);

  useEffect(() => {
    try {
      sessionStorage.setItem(
        STORAGE_KEY,
        JSON.stringify({ ...state, version: STORAGE_SCHEMA_VERSION })
      );
    } catch (e) {
      console.error('Failed to save offer builder to storage:', e);
    }
  }, [state]);

  const addItem = useCallback((side: OfferSide, item: OfferLineItem) => {
    setState((prev) => {
      const items = prev[side];
      if (items.length >= TRADE_OFFER_MAX_CARDS_PER_SIDE) return prev;
      if (items.some((i) => i.userMTGCardHaveId === item.userMTGCardHaveId)) return prev;
      return { ...prev, [side]: [...items, item] };
    });
  }, []);

  const removeItem = useCallback((side: OfferSide, userMTGCardHaveId: number) => {
    setState((prev) => ({
      ...prev,
      [side]: prev[side].filter((i) => i.userMTGCardHaveId !== userMTGCardHaveId),
    }));
  }, []);

  const markRowCreatedByBuilder = useCallback(
    (userMTGCardHaveId: number, rowQuantityAfterAdd: number, addedQuantity = 1) => {
      // The row is ours to delete only if it did not exist before this add.
      const wasCreated = rowQuantityAfterAdd === addedQuantity;
      setState((prev) => {
        const rest = (prev.createdRows || []).filter(
          (r) => r.userMTGCardHaveId !== userMTGCardHaveId
        );
        return {
          ...prev,
          createdRows: [...rest, { userMTGCardHaveId, addedQuantity, wasCreated }],
        };
      });
    },
    []
  );

  const getBuilderCreatedRow = useCallback(
    (userMTGCardHaveId: number) =>
      (state.createdRows || []).find((r) => r.userMTGCardHaveId === userMTGCardHaveId),
    [state.createdRows]
  );

  const forgetBuilderCreatedRow = useCallback((userMTGCardHaveId: number) => {
    setState((prev) => ({
      ...prev,
      createdRows: (prev.createdRows || []).filter(
        (r) => r.userMTGCardHaveId !== userMTGCardHaveId
      ),
    }));
  }, []);

  const setItemCondition = useCallback((userMTGCardHaveId: number, condition: string) => {
    setState((prev) => ({
      ...prev,
      youSend: prev.youSend.map((i) => {
        if (i.userMTGCardHaveId !== userMTGCardHaveId) return i;
        // No price map (seeded item) or unpriced condition: refuse the change
        // rather than price a declaration from the wrong condition's quote.
        const price =
          i.quotedPricesByCondition?.[condition] ??
          (condition === i.condition ? i.rowQuotedPricePoints : null);
        if (price == null) return i;
        return { ...i, declaredCondition: condition, unitPricePoints: price };
      }),
    }));
  }, []);

  /**
   * Clamped by TWO ceilings: what the sender owns, and what the recipient's
   * want record still has room for. The want ceiling applies only to lines
   * bound to a want entry — seeded lines (suggestions, the have-list hover)
   * and receive-side lines carry no binding and keep the owned-only clamp.
   *
   * The clamp lives here as well as on the stepper's disabled state so the
   * invariant survives a caller that never renders that button, matching the
   * defensive shape the owned-quantity clamp already had.
   */
  const setItemQuantity = useCallback(
    (side: OfferSide, userMTGCardHaveId: number, quantity: number) => {
      setState((prev) => {
        const target = prev[side].find((i) => i.userMTGCardHaveId === userMTGCardHaveId);
        if (!target) return prev;

        let ceiling = target.ownedQuantity;
        if (target.originatingWantEntryId != null) {
          // Budget spent by the OTHER lines on this want; this line's own
          // quantity is what we are replacing, so it must not count against it.
          const spentElsewhere = prev[side].reduce(
            (sum, i) =>
              i.originatingWantEntryId === target.originatingWantEntryId &&
              i.userMTGCardHaveId !== userMTGCardHaveId
                ? sum + i.quantity
                : sum,
            0
          );
          const wantCeiling = (target.wantEntryQuantity ?? Infinity) - spentElsewhere;
          ceiling = Math.min(ceiling, wantCeiling);
        }

        // Never below 1: a line at zero is a removal, which is the ×'s job.
        const next = Math.max(1, Math.min(quantity, ceiling));
        if (next === target.quantity) return prev;
        return {
          ...prev,
          [side]: prev[side].map((i) =>
            i.userMTGCardHaveId === userMTGCardHaveId ? { ...i, quantity: next } : i
          ),
        };
      });
    },
    []
  );

  // After the have-list quantity editor saves (conflict dialog option 3), the
  // line item's owned quantity must track the new truth or the conflict check
  // would keep firing from stale data.
  const updateItemOwnedQuantity = useCallback(
    (side: OfferSide, userMTGCardHaveId: number, ownedQuantity: number) => {
      setState((prev) => ({
        ...prev,
        [side]: prev[side].map((i) =>
          i.userMTGCardHaveId === userMTGCardHaveId
            ? { ...i, ownedQuantity, quantity: Math.min(i.quantity, Math.max(1, ownedQuantity)) }
            : i
        ),
      }));
    },
    []
  );

  const getAddedQuantity = useCallback(
    (side: OfferSide, userMTGCardHaveId: number) => {
      const item = state[side].find((i) => i.userMTGCardHaveId === userMTGCardHaveId);
      return item ? item.quantity : 0;
    },
    [state]
  );

  // Sums `quantity`, not line count: one line of 3 copies spends the same
  // budget as three lines of 1, and both are reachable (three printings vs the
  // column's stepper). Counting lines would close only half the hole.
  const getWantEntryAddedQuantity = useCallback(
    (side: OfferSide, wantEntryId: number) =>
      state[side].reduce(
        (sum, i) => (i.originatingWantEntryId === wantEntryId ? sum + i.quantity : sum),
        0
      ),
    [state]
  );

  /**
   * Lives outside `state` so it is never persisted, and so changing it cannot
   * rewrite the package. One failed submit, not a property of the offer.
   */
  const [rejectedHaveId, setRejectedHaveId] = useState<number | null>(null);

  // Any edit to either side invalidates the server's verdict — the package it
  // judged no longer exists — so the mark clears itself rather than lingering
  // on a line the user has already dealt with.
  useEffect(() => {
    setRejectedHaveId(null);
  }, [state.youSend, state.theySend]);

  const clearAll = useCallback(() => {
    setState((prev) => ({
      ...prev,
      youSend: [],
      theySend: [],
      suggestionId: undefined,
      // Marks are dropped, never acted on: clearAll runs after a SUCCESSFUL
      // send, where the created rows are exactly the inventory the sender just
      // promised. Undoing them there would delete cards out of a live offer.
      createdRows: [],
    }));
    try {
      sessionStorage.removeItem(STORAGE_KEY);
    } catch {
      // nothing to clear
    }
  }, []);

  const youSendTotal = useMemo(() => sideTotal(state.youSend), [state.youSend]);
  const theySendTotal = useMemo(() => sideTotal(state.theySend), [state.theySend]);

  const value: OfferBuilderContextType = {
    counterpartyId,
    counterpartyUsername,
    // Live props, deliberately bypassing `state` so they never persist
    viewerPoints,
    counterpartyPoints,
    youSend: state.youSend,
    theySend: state.theySend,
    suggestionId: state.suggestionId,
    youSendTotal,
    theySendTotal,
    youSendAtCap: state.youSend.length >= TRADE_OFFER_MAX_CARDS_PER_SIDE,
    theySendAtCap: state.theySend.length >= TRADE_OFFER_MAX_CARDS_PER_SIDE,
    addItem,
    removeItem,
    setItemCondition,
    setItemQuantity,
    updateItemOwnedQuantity,
    getAddedQuantity,
    getWantEntryAddedQuantity,
    markRowCreatedByBuilder,
    getBuilderCreatedRow,
    forgetBuilderCreatedRow,
    rejectedHaveId,
    setRejectedHaveId,
    clearAll,
  };

  return <OfferBuilderContext.Provider value={value}>{children}</OfferBuilderContext.Provider>;
};

export const useOfferBuilder = (): OfferBuilderContextType => {
  const context = useContext(OfferBuilderContext);
  if (!context) {
    throw new Error('useOfferBuilder must be used within an OfferBuilderProvider');
  }
  return context;
};

export default OfferBuilderContext;
